import React, { useCallback, useMemo, useState } from 'react';
import TrueImpactModal, {
  modalHeightAutoStyles,
  modalTitleStyles,
} from 'shared/design-system/components/TrueImpactModal';
import styled from 'styled-components';
import {
  faClone,
  faCalendarLines,
  faTrash,
  faEye,
} from '@fortawesome/pro-light-svg-icons';
import { SelectorOptions } from 'shared/design-system/components/Selector';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { ReportsTable } from '../ReportGroup';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import { Program } from '../../Reports/NewReportDialog';
import CloneModal from '../CloneModal';
import ChangeProgramModal from './ChangeProgramModal';
import { AllReportYears } from '../api';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { capitalizeOnlyFirstLetter } from 'shared/utils/stringUtils';
import TrueImpactDebug from '../../ReportBuilder/TrueImpactDebug';
import { useFeatureFlags } from 'shared/providers/FeatureFlags/useFeatureFlags';
import { useUserActions } from 'shared/auth/hooks/useUserActions';

interface MoreItemModalProps {
  index: number;
  open: boolean;
  programsList: RequestState<{
    allPrograms: Program[];
  }>;
  reportsState: RequestState<AllReportYears>;
  report: ReportsTable;
  handleClose: () => unknown;
  handleReloadReportPage: () => unknown;
  onDelete: (reportId: string) => unknown;
}

const StyledDiv = styled.div`
  padding: ${SPACERS.large};
  .report-info-details {
    display: flex;
    gap: ${SPACERS.medium};
    justify-content: space-between;
    margin-bottom: ${SPACERS.medium};
  }
`;

const MoreItemModal: React.FC<MoreItemModalProps> = ({
  open,
  index,
  report,
  reportsState,
  programsList,
  handleClose,
  handleReloadReportPage,
  onDelete,
}) => {
  const reportVisibility = useFeatureFlags('reportVisibility');
  useUserActions();
  const [isDebug, setIsDebug] = useState<boolean>(false);
  const [openClone, setOpenClone] = useState<boolean>(false);
  const [openChangeProgram, setOpenChangeProgram] = useState<boolean>(false);

  const handleDone = useCallback(() => {
    handleClose();
    setOpenClone(false);
    setOpenChangeProgram(false);
  }, [handleClose, setOpenChangeProgram, setOpenClone]);

  const handleUpdateVisibility = useCallback(() => {
    window.location.href = '/account/report-sharing';
  }, []);

  const isDraft = report?.status === 'Draft';

  const options = useMemo(
    () =>
      [
        reportVisibility
          ? {
              label: 'Update report visibility',
              onClick: handleUpdateVisibility,
              icon: faEye,
            }
          : undefined,
        {
          label: 'Clone this report',
          onClick: () => setOpenClone(true),
          icon: faClone,
        },
        {
          label: 'Change program of this report',
          onClick: () => setOpenChangeProgram(true),
          icon: faCalendarLines,
        },
        isDraft
          ? {
              label: 'Delete this report',
              onClick: () => {
                onDelete(report?.dbId?.toString() || '');
                handleClose();
              },
              icon: faTrash,
            }
          : undefined,
      ].filter(Boolean),
    [
      setOpenClone,
      setOpenChangeProgram,
      onDelete,
      report?.dbId,
      handleClose,
      isDraft,
    ],
  );

  return (
    <>
      <TrueImpactModal
        open={open}
        maxWidth="700px"
        title={`${report.program.name} (${report.year})`}
        hideFooter={true}
        onClose={handleClose}
        addStyles={modalHeightAutoStyles + modalTitleStyles}
      >
        <StyledDiv>
          <TrueImpactDebug isDebug={isDebug} handleChange={setIsDebug} />
          {isDebug && (
            <div className="report-info-details">
              <TrueImpactText>
                <strong>Status:</strong>
                <br />
                {report.status}
              </TrueImpactText>
              <TrueImpactText>
                <strong>Stage:</strong>
                <br />
                {capitalizeOnlyFirstLetter(report.stage)}
              </TrueImpactText>
              <TrueImpactText>
                <strong>Feedback Type:</strong>
                <br />
                {report.feedbackType}
              </TrueImpactText>
            </div>
          )}
          <SelectorOptions
            variant="secondary"
            options={options}
            getIcon={e => e?.icon}
            getLabel={e => e?.label}
            getSelected={() => false}
            onClick={e => {
              e?.onClick();
            }}
          />
        </StyledDiv>
      </TrueImpactModal>
      {openChangeProgram && (
        <ChangeProgramModal
          report={report}
          reportsState={reportsState}
          programsList={programsList}
          handleClose={handleDone}
          handleReloadReportPage={handleReloadReportPage}
        />
      )}
      {openClone && (
        <CloneModal
          report={report}
          programsList={programsList}
          handleClose={handleDone}
        />
      )}
    </>
  );
};

export default React.memo(MoreItemModal);
